<template>
    <div class="report-management-container">
        <!-- Header Section -->
        <el-card class="header-card" shadow="never">
            <el-row :gutter="20" align="middle">
                <el-col :span="24">
                    <h2 style="margin: 0; color: #303133;">Laporan Data</h2>
                    <p style="margin: 5px 0 0 0; color: #909399; font-size: 14px;">Buat laporan dan ekspor data dari berbagai modul sistem</p>
                </el-col>
            </el-row>
        </el-card>

        <!-- Filter Section -->
        <el-card class="filter-card" shadow="never">
            <el-row :gutter="20" align="middle">
                <el-col :md="6">
                    <el-select 
                        v-model="selectedFlow.id" 
                        clearable 
                        filterable 
                        placeholder="Pilih modul data"
                        @change="changeFlow"
                        style="width: 100%"
                        :loading="loadingFlows"
                    >
                        <el-option 
                            v-for="item in listFlows" 
                            :key="item.id"
                            :label="item.group_name + ' - ' + item.flow_name" 
                            :value="item.id" 
                        />
                    </el-select>
                </el-col>
                <el-col :md="6">
                    <el-select 
                        v-model="selectedFlow_fields" 
                        multiple 
                        filterable 
                        collapse-tags 
                        collapse-tags-tooltip
                        clearable 
                        placeholder="Pilih kolom yang akan ditampilkan"
                        style="width: 100%"
                        :disabled="!selectedFlow.id"
                    >
                        <el-option 
                            v-for="item in listFields" 
                            :key="item.field_name" 
                            :label="item.field_title || item.field_name"
                            :value="item.field_name" 
                        />
                    </el-select>
                </el-col>
                <el-col :md="6">
                    <el-input 
                        v-model="q" 
                        placeholder="Cari data..." 
                        clearable
                        @keyup.enter="cariData"
                        :prefix-icon="Search"
                        :disabled="!selectedFlow.id"
                    />
                </el-col>
                <el-col :md="6">
                    <el-button-group>
                        <el-button 
                            type="primary"
                            @click="cariData"
                            :icon="Search"
                            :loading="loading"
                            :disabled="!selectedFlow.id || selectedFlow_fields.length === 0"
                        >
                            Cari
                        </el-button>
                        <el-button 
                            type="success"
                            v-if="tableData.length > 0" 
                            @click="exportCSV"
                            :icon="Download"
                        >
                            Ekspor CSV
                        </el-button>
                    </el-button-group>
                </el-col>
            </el-row>

            <!-- Date Filter Row -->
            <el-row :gutter="20" align="middle" style="margin-top: 20px;" v-if="selectedFlow.id">
                <el-col :md="6">
                    <el-date-picker
                        v-model="dateRange"
                        type="daterange"
                        range-separator="s/d"
                        start-placeholder="Tanggal mulai"
                        end-placeholder="Tanggal akhir"
                        format="DD-MM-YYYY"
                        value-format="YYYY-MM-DD"
                        style="width: 100%"
                        :shortcuts="dateShortcuts"
                    />
                </el-col>
                <el-col :md="18" style="text-align: right;">
                    <el-text type="info">
                        <el-icon><InfoFilled /></el-icon>
                        Total: {{ tableCount }} data
                    </el-text>
                </el-col>
            </el-row>
        </el-card>

        <!-- Data Table Section -->
        <el-card shadow="never" v-if="selectedFlow.id">
            <el-table 
                v-if="listFieldsSelected.length > 0" 
                :data="showData" 
                v-loading="loading"
                empty-text="Tidak ada data"
                :stripe="true"
                @sort-change="sortData" 
                style="width: 100%" 
                show-summary 
                :summary-method="getSummaries"
                :height="600"
            >
                <el-table-column type="index" width="60" label="No" align="center" />
                <el-table-column 
                    v-for="field in listFieldsSelected" 
                    :key="field.field_name" 
                    :prop="field.field_name"
                    :label="field.field_title || field.field_name" 
                    sortable="custom"
                    :align="(field.field_type == 'number') ? 'right' : 'left'"
                    :min-width="getColumnWidth(field)"
                    show-overflow-tooltip
                >
                    <template #default="scope">
                        <span v-if="field.field_type === 'number'">
                            {{ formatNumber(scope.row[field.field_name]) }}
                        </span>
                        <span v-else-if="field.field_type === 'datetime'">
                            {{ formatDate(scope.row[field.field_name]) }}
                        </span>
                        <span v-else>
                            {{ scope.row[field.field_name] }}
                        </span>
                    </template>
                </el-table-column>
            </el-table>

            <div v-else class="empty-state">
                <el-empty description="Pilih kolom yang akan ditampilkan untuk melihat data" />
            </div>

            <!-- Pagination -->
            <div v-if="tableData.length > 0" class="pagination-container">
                <el-pagination 
                    layout="total, sizes, prev, pager, next, jumper" 
                    v-model:page-size="pagination.page_size" 
                    :page-sizes="[20, 50, 100, 200]"
                    :total="tableCount" 
                    v-model:current-page="pagination.page"
                    :background="true"
                />
            </div>
        </el-card>

        <!-- Empty State when no flow selected -->
        <el-card shadow="never" v-else>
            <el-empty description="Pilih modul data untuk memulai membuat laporan" />
        </el-card>

    </div>
</template>

<script>
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { 
    Search, Download, InfoFilled
} from '@element-plus/icons-vue'

export default {
    name: "ReportView",
    data() {
        return {
            Search, Download, InfoFilled,
            listFlows: [],
            selectedFlow: { id: null, fields: [] },
            selectedFlow_fields: [],
            q: '',
            dateRange: null,
            pagination: { count: 0, total_count: 0, page: 1, total_page: 1, page_size: 20 },
            listFields: [],
            tableData: [],
            tableCount: 0,
            tableOrder: {},
            loading: false,
            loadingFlows: false,
            dateShortcuts: [
                {
                    text: 'Minggu ini',
                    value: () => {
                        const end = new Date()
                        const start = new Date()
                        start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
                        return [start, end]
                    }
                },
                {
                    text: 'Bulan ini',
                    value: () => {
                        const end = new Date()
                        const start = new Date()
                        start.setDate(1)
                        return [start, end]
                    }
                },
                {
                    text: '3 bulan terakhir',
                    value: () => {
                        const end = new Date()
                        const start = new Date()
                        start.setMonth(start.getMonth() - 3)
                        return [start, end]
                    }
                }
            ]
        }
    },
    computed: {
        apiHeaders() {
            return {
                'Authorization': `Bearer ${this.$store.state.auth_token}`
            }
        },
        baseUrl() {
            return this.$store.state.base_url
        },
        showData() {
            const totalItems = this.tableData.length
            const startIndex = (this.pagination.page - 1) * this.pagination.page_size
            const endIndex = Math.min(startIndex + this.pagination.page_size, totalItems)
            return this.tableData.slice(startIndex, endIndex)
        },
        listFieldsSelected() {
            let selected = []
            this.selectedFlow_fields.forEach((selectedField) => {
                const field = this.listFields.find(f => f.field_name === selectedField)
                if (field) {
                    selected.push(field)
                }
            })
            return selected
        }
    },
    created() {
        this.getProfile()
    },
    methods: {
        async getProfile() {
            this.loadingFlows = true
            try {
                const response = await axios.get(`${this.baseUrl}/api/user/${localStorage.usereid}`, {
                    headers: this.apiHeaders
                })
                this.listFlows = response.data.access_flows || []
            } catch (error) {
                this.handleError(error, 'Gagal mengambil data profil')
            } finally {
                this.loadingFlows = false
            }
        },
        
        async getField(table_name) {
            try {
                const response = await axios.get(`${this.baseUrl}/api/spf/fields/${table_name}`, {
                    headers: this.apiHeaders
                })
                this.listFields = response.data.data
            } catch (error) {
                this.handleError(error, 'Gagal mengambil data kolom')
            }
        },
        
        async fetchData() {
            this.loading = true
            try {
                let params = {
                    limit: 1000,
                    table_name: this.selectedFlow.table_name,
                    fields: this.selectedFlow_fields.length > 0 ? this.selectedFlow_fields.join(',') : 'id',
                    q: this.q
                }
                
                if (this.tableOrder.orderBy) {
                    params.order_by = this.tableOrder.orderBy
                    params.order_type = this.tableOrder.orderType
                }
                
                if (this.dateRange && this.dateRange.length === 2) {
                    params.date_start = this.dateRange[0]
                    params.date_end = this.dateRange[1]
                }
                
                const response = await axios.get(`${this.baseUrl}/api/data`, {
                    params,
                    headers: this.apiHeaders
                })
                
                this.tableData = response.data.data
                this.tableCount = response.data.count
                this.pagination.page = 1
            } catch (error) {
                this.handleError(error, 'Gagal mengambil data')
            } finally {
                this.loading = false
            }
        },
        
        sortData(column) {
            this.tableOrder = { orderBy: column.prop }
            if (column.order === 'ascending') {
                this.tableOrder.orderType = 'asc'
            } else {
                this.tableOrder.orderType = 'desc'
            }
            this.fetchData()
        },
        
        cariData() {
            this.tableOrder = {}
            this.fetchData()
        },
        
        changeFlow(val) {
            const flow = this.listFlows.find(f => f.id === val)
            if (flow) {
                this.selectedFlow = flow
                this.selectedFlow_fields = []
                this.tableData = []
                this.tableCount = 0
                this.dateRange = null
                this.q = ''
                this.getField(this.selectedFlow.table_name)
            }
        },
        
        getSummaries(params) {
            const { columns, data } = params
            const sums = []
            
            columns.forEach((column, index) => {
                if (index === 0) {
                    sums[index] = 'Total'
                    return
                }
                
                if (index === 1) {
                    sums[index] = ''
                    return
                }
                
                const field = this.listFieldsSelected.find(f => f.field_name === column.property)
                if (field && field.field_type === 'number') {
                    const values = data.map(item => Number(item[column.property]))
                    if (!values.every(value => isNaN(value))) {
                        sums[index] = values.reduce((prev, curr) => {
                            const value = Number(curr)
                            if (!isNaN(value)) {
                                return prev + curr
                            } else {
                                return prev
                            }
                        }, 0)
                        sums[index] = this.formatNumber(sums[index])
                    } else {
                        sums[index] = ''
                    }
                } else {
                    sums[index] = ''
                }
            })
            
            return sums
        },
        
        formatNumber(value) {
            if (value === null || value === undefined) return ''
            return new Intl.NumberFormat('id-ID').format(value)
        },
        
        formatDate(value) {
            if (!value) return ''
            const date = new Date(value)
            return new Intl.DateTimeFormat('id-ID', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date)
        },
        
        getColumnWidth(field) {
            if (field.field_type === 'number') return 120
            if (field.field_type === 'datetime') return 160
            return 150
        },
        
        exportCSV() {
            const dataToExport = this.tableData
            const fields = this.listFieldsSelected.map(f => f.field_title || f.field_name)
            const rows = []
            
            dataToExport.forEach((rec) => {
                const row = []
                this.listFieldsSelected.forEach((field) => {
                    let value = rec[field.field_name]
                    if (field.field_type === 'number') {
                        value = this.formatNumber(value)
                    } else if (field.field_type === 'datetime') {
                        value = this.formatDate(value)
                    }
                    // Escape quotes and wrap in quotes if contains comma
                    if (value && value.toString().includes(',')) {
                        value = `"${value.toString().replace(/"/g, '""')}"`
                    }
                    row.push(value || '')
                })
                rows.push(row)
            })
            
            let csvContent = "\uFEFF" // UTF-8 BOM
            csvContent += fields.join(",") + "\r\n"
            rows.forEach((rowArray) => {
                csvContent += rowArray.join(",") + "\r\n"
            })
            
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
            const link = document.createElement("a")
            const url = URL.createObjectURL(blob)
            link.setAttribute("href", url)
            link.setAttribute("download", `${this.selectedFlow.flow_name}_${new Date().toISOString().slice(0, 10)}.csv`)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            
            ElMessage({
                message: 'Data berhasil diekspor!',
                type: 'success'
            })
        },
        
        handleError(error, defaultMessage = '') {
            const message = error.response?.data?.message || defaultMessage || 'Terjadi kesalahan'
            ElMessage({
                message,
                type: 'error'
            })
            console.error('Error:', error)
        }
    }
}
</script>

<style scoped>
.report-management-container {
    padding: 20px;
}

.header-card {
    margin-bottom: 20px;
}

.filter-card {
    margin-bottom: 20px;
}

.empty-state {
    padding: 40px;
    text-align: center;
}

.pagination-container {
    margin-top: 20px;
    display: flex;
    justify-content: center;
}

.el-table {
    font-size: 14px;
}

@media (max-width: 768px) {
    .report-management-container {
        padding: 10px;
    }
    
    .el-dialog {
        width: 90% !important;
    }
}
</style>